import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from 'store/hooks'
import { UseMutationResult } from '@tanstack/react-query'
import { InformationForm, PasswordForm } from 'models/account'
import { ROUTES } from '../../../../constants'
import { selectProfile } from 'lib/helpers/selectors'
import useProfile, {
  useUpdateInformation,
  useUpdatePassword,
} from 'hooks/queries/account'
import useDialog from 'hooks/Shared/useDialog'
import { TaskAlt } from 'components/Shared/CustomIcons'

export interface IUseProfileForm {
  name: string
  lastName: string
  email: string
  emailCobza1: string
  emailCobza2: string
  newPassword: string
  isFormValid: boolean
  updateInfoMutation: UseMutationResult<void, any, InformationForm, unknown>
  updatePassMutation: UseMutationResult<void, any, PasswordForm, unknown>
  setName: Dispatch<SetStateAction<string>>
  setLastName: Dispatch<SetStateAction<string>>
  setEmail: Dispatch<SetStateAction<string>>
  setEmailCobza1: Dispatch<SetStateAction<string>>
  setEmailCobza2: Dispatch<SetStateAction<string>>
  setCurrentPassword: Dispatch<SetStateAction<string>>
  setNewPassword: Dispatch<SetStateAction<string>>
  setConfirmPassword: Dispatch<SetStateAction<string>>
  setValidRules: Dispatch<SetStateAction<boolean>>
  onSubmit: (form: any) => Promise<void>
}

const useProfileForm = (): IUseProfileForm => {
  const dialog = useDialog()
  const navigate = useNavigate()

  const profile = useAppSelector(selectProfile)

  const [name, setName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [emailCobza1, setEmailCobza1] = useState('')
  const [emailCobza2, setEmailCobza2] = useState('')

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const [validName, setValidName] = useState(false)
  const [validPassword, setValidPassword] = useState(true)
  const [validRules, setValidRules] = useState(false)
  const [validEmails, setValidEmails] = useState(false)

  const [isFormValid, setIsFormValid] = useState(false)

  const profileQuery = useProfile({
    enabled: false,
    onError: () => navigate(ROUTES.HOME, { replace: true }),
  })
  const updateInfoMutation = useUpdateInformation({
    onSuccess: () => {
      profileQuery.refetch()
    },
  })

  const updatePassMutation = useUpdatePassword({
    onSuccess: () => {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    },
  })

  const onSubmit = async (form: any) => {
    if (validName && validEmails) await updateInfoMutation.mutateAsync(form)
    if (validPassword && validRules) await updatePassMutation.mutateAsync(form)

    await dialog.primary({
      subtitle: 'Tus cambios se guardaron con éxito',
      title: '¡Hecho!',
      icon: TaskAlt,
      onPrimaryClick: () => window.location.reload(),
    })
  }

  useEffect(() => {
    setName(profile.name)
    setLastName(profile.lastname)
    setEmail(profile.email)
    setEmailCobza1(profile.emailCobza1)
    setEmailCobza2(profile.emailCobza2)
  }, [
    profile.name,
    profile.lastname,
    profile.email,
    profile.emailCobza1,
    profile.emailCobza2,
  ])

  useEffect(() => {
    const noEmptyNames = name.length >= 2 && lastName.length >= 2
    setValidName(noEmptyNames)
  }, [profile?.name, profile?.lastname, name, lastName])

  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    const noEmptyEmails = email !== '' && emailCobza1 !== ''

    const noInvalidEmails = emailCobza2
      ? emailRegex.test(email) &&
        emailRegex.test(emailCobza1) &&
        emailRegex.test(emailCobza2)
      : emailRegex.test(email) && emailRegex.test(emailCobza1)

    const noDuplicateEmailCobranza =
      emailCobza2 !== '' ? emailCobza2 !== emailCobza1 : emailCobza1 !== ''
    setValidEmails(noEmptyEmails && noInvalidEmails && noDuplicateEmailCobranza)
  }, [email, emailCobza1, emailCobza2])

  useEffect(() => {
    const noEmptyCurrentPassword = currentPassword !== ''
    const noEmptyConfirmPassword =
      newPassword !== '' ? confirmPassword !== '' : true
    const duplicateNewPassword = newPassword === currentPassword
    const isConfirmedPasswordEqual = confirmPassword === newPassword

    setValidPassword(
      noEmptyCurrentPassword &&
        noEmptyConfirmPassword &&
        !duplicateNewPassword &&
        isConfirmedPasswordEqual
    )
  }, [currentPassword, newPassword, confirmPassword])

  useEffect(() => {
    const hasDataChanged =
      name !== profile.name ||
      lastName !== profile.lastname ||
      email !== profile.email ||
      emailCobza1 !== profile.emailCobza1 ||
      emailCobza2 !== profile.emailCobza2 ||
      newPassword !== ''

    hasDataChanged
      ? setIsFormValid(
          newPassword !== ''
            ? validName && validEmails && validPassword && validRules
            : validName && validEmails
        )
      : setIsFormValid(false)
  }, [
    newPassword,
    validEmails,
    validName,
    validPassword,
    validRules,
    email,
    emailCobza1,
    emailCobza2,
    name,
    lastName,
    profile.email,
    profile.emailCobza1,
    profile.emailCobza2,
    profile.name,
    profile.lastname,
  ])

  return {
    name,
    lastName,
    email,
    emailCobza1,
    emailCobza2,
    newPassword,
    isFormValid,
    updateInfoMutation,
    updatePassMutation,
    setName,
    setLastName,
    setEmail,
    setEmailCobza1,
    setEmailCobza2,
    setCurrentPassword,
    setNewPassword,
    setConfirmPassword,
    setValidRules,
    onSubmit,
  }
}

export default useProfileForm
